"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Info, Loader2, RotateCcw, Search, X } from "lucide-react";
import type { Agent, CartesiaVoice } from "@/lib/api";
import { api } from "@/lib/api";
import {
  TTS_EMOTION_MORE,
  TTS_EMOTION_PRIMARY,
  TTS_GENDER_OPTS,
  TTS_LANGUAGE_OPTS,
  clampTtsSpeed,
  clampTtsVolume,
} from "@/lib/voice-catalog";
import { useCartesiaAudioPlayer, VoicePlayButton } from "@/components/voice-play-button";
import { Select } from "@/components/ui/select";

type ListCacheEntry = {
  voices: CartesiaVoice[];
  hasMore: boolean;
  nextPage: string | null;
  at: number;
};

const LIST_CACHE = new Map<string, ListCacheEntry>();
const LIST_CACHE_TTL_MS = 5 * 60 * 1000;

function listCacheKey(q: string, gender: string, language: string) {
  return `${q}\0${gender}\0${language}`;
}

function normalizeVoice(raw: unknown): CartesiaVoice | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  const id = String(v.id || "").trim();
  if (!id) return null;
  let name = String(v.name || v.display_name || v.voice_name || "").trim();
  let tagline = String(v.tagline || "").trim();
  if (!tagline && name.includes(" - ")) {
    const parts = name.split(" - ");
    name = parts[0].trim();
    tagline = parts.slice(1).join(" - ").trim();
  }
  if (!name) name = `Voice ${id.slice(0, 8)}`;
  return {
    id,
    name,
    tagline,
    description: String(v.description || "").trim(),
    gender: (v.gender as string) || null,
    language: (v.language as string) || null,
    locale: (v.locale as string) || null,
    accent: (v.accent as string) || null,
    country: (v.country as string) || null,
    hasPreview: v.hasPreview !== false,
    isPro: Boolean(v.isPro ?? v.is_pro),
  };
}

function voiceTitle(v: CartesiaVoice): string {
  return String(v.name || "").trim() || `Voice ${(v.id || "").slice(0, 8) || "—"}`;
}

function genderChip(g: string | null | undefined): string | null {
  if (g === "feminine") return "Female";
  if (g === "masculine") return "Male";
  if (g === "gender_neutral") return "Neutral";
  return null;
}

function languageChip(v: CartesiaVoice): string | null {
  const loc = (v.locale || v.language || "").trim();
  if (!loc) return null;
  if (loc === "en" || loc === "en-US") return "English (US)";
  if (loc === "en-GB") return "English (UK)";
  return loc;
}

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
};

export function AgentEditVoicePanel({ draft, onChange }: Props) {
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [gender, setGender] = useState("");
  const [language, setLanguage] = useState("en");
  const [voices, setVoices] = useState<CartesiaVoice[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const loadGenRef = useRef(0);
  const player = useCartesiaAudioPlayer();

  const speed = clampTtsSpeed(draft.ttsSpeed, 1);
  const volume = clampTtsVolume(draft.ttsVolume, 1);
  const emotion = draft.ttsEmotion || "";

  useEffect(() => {
    const t = window.setTimeout(() => setQDebounced(q.trim()), 280);
    return () => window.clearTimeout(t);
  }, [q]);

  const applyList = useCallback((entry: ListCacheEntry) => {
    setVoices(entry.voices);
    setHasMore(entry.hasMore);
    setNextPage(entry.nextPage);
  }, []);

  const loadVoices = useCallback(async (opts?: {
    append?: boolean;
    cursor?: string | null;
    force?: boolean;
  }) => {
    const append = Boolean(opts?.append);
    const key = listCacheKey(qDebounced, gender, language);
    const cached = LIST_CACHE.get(key);
    const fresh = cached && Date.now() - cached.at < LIST_CACHE_TTL_MS;
    const cacheUsable = Boolean(
      fresh
      && cached
      && cached.voices.length > 0
      && cached.voices.some((v) => String(v.name || "").trim()),
    );

    if (!append && !opts?.force && cacheUsable && cached) {
      applyList(cached);
      setLoading(false);
      setListError(null);
      return;
    }

    const gen = ++loadGenRef.current;
    if (append) setLoadingMore(true);
    else if (!cacheUsable) setLoading(true);
    setListError(null);
    try {
      const res = await api.listCartesiaVoices({
        q: qDebounced || undefined,
        gender: gender || undefined,
        language: language || undefined,
        limit: 36,
        startingAfter: opts?.cursor || undefined,
      });
      if (gen !== loadGenRef.current) return;
      const incoming = (Array.isArray(res.voices) ? res.voices : [])
        .map(normalizeVoice)
        .filter((v): v is CartesiaVoice => Boolean(v));
      const merged = append
        ? [...(LIST_CACHE.get(key)?.voices || voices), ...incoming]
        : incoming;
      const seen = new Set<string>();
      const deduped: CartesiaVoice[] = [];
      for (const v of merged) {
        if (seen.has(v.id)) continue;
        seen.add(v.id);
        deduped.push(v);
      }
      const entry: ListCacheEntry = {
        voices: deduped,
        hasMore: Boolean(res.hasMore),
        nextPage: res.nextPage || null,
        at: Date.now(),
      };
      LIST_CACHE.set(key, entry);
      applyList(entry);
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setListError(e instanceof Error ? e.message : "Failed to load voices");
      if (!append && !cacheUsable) setVoices([]);
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [applyList, gender, language, qDebounced, voices]);

  useEffect(() => {
    if (!libraryOpen) return;
    void loadVoices({ force: true });
  }, [libraryOpen, qDebounced, gender, language]); // eslint-disable-line react-hooks/exhaustive-deps

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  const selected = useMemo(
    () => voices.find((v) => v.id === draft.voiceId) || null,
    [voices, draft.voiceId],
  );

  const displayName = draft.voiceName || selected?.name || (draft.voiceId ? "Custom voice" : "Workspace default");
  const displayDesc = selected?.description || selected?.tagline || (
    draft.voiceId
      ? "Selected Cartesia voice for agent replies."
      : "Uses the workspace default voice until you pick one."
  );
  const gChip = genderChip(selected?.gender);
  const langChip = selected ? languageChip(selected) : null;

  const selectedPlayKey = draft.voiceId ? `stock:${draft.voiceId}` : "";
  const selectedPlayState = selectedPlayKey
    ? player.stateFor(selectedPlayKey)
    : "idle";

  return (
    <div className={`agent-voice${libraryOpen ? " has-library" : ""}`}>
      <div className="agent-voice-main">
        <div className="agent-eot-title-row">
          <div>
            <div className="agent-voice-title-line">
              <h3 className="agent-conv-h">Voice</h3>
              <span className="agent-voice-autosave-hint" title="Voice fields save with Save configuration">
                <Info size={12} aria-hidden />
                Saves with configuration
              </span>
            </div>
            <p className="agent-conv-guards-lead">
              Configure the voice used for agent responses during calls.
            </p>
          </div>
          {!libraryOpen && (
            <button
              type="button"
              className="agent-guards-tips-link"
              onClick={() => setLibraryOpen(true)}
            >
              Choose a voice →
            </button>
          )}
        </div>

        <section className="agent-voice-selected-card">
          <div className="agent-voice-selected-row">
            <VoicePlayButton
              variant="solid"
              playState={draft.voiceId ? selectedPlayState : "idle"}
              disabled={!draft.voiceId}
              aria-label={
                !draft.voiceId
                  ? "Select a voice to preview"
                  : selectedPlayState === "playing"
                    ? "Pause voice preview"
                    : "Play voice preview"
              }
              onClick={() => {
                if (draft.voiceId) void player.playStockPreview(draft.voiceId);
              }}
            />
            <div className="agent-voice-selected-copy">
              <b>{displayName}</b>
              <p>{displayDesc}</p>
              <div className="agent-voice-chips">
                {langChip ? <span className="agent-voice-chip agent-voice-chip--lang">{langChip}</span> : null}
                {gChip ? (
                  <span className={`agent-voice-chip agent-voice-chip--${gChip.toLowerCase()}`}>
                    {gChip}
                  </span>
                ) : null}
              </div>
              {draft.voiceId ? (
                <code className="agent-voice-id">{draft.voiceId}</code>
              ) : (
                <span className="agent-voice-id-muted">No voice ID — workspace default</span>
              )}
            </div>
          </div>
          {draft.voiceId ? (
            <div className="agent-voice-clear-wrap">
              <button
                type="button"
                className="agent-voice-clear"
                onClick={() => patch({ voiceId: null, voiceName: null })}
              >
                Clear selection
              </button>
              <p className="agent-voice-clear-hint">Clear to use workspace default.</p>
            </div>
          ) : null}
        </section>

        {(player.error && player.activeKey?.startsWith("stock:")) || listError ? (
          <p className="agent-voice-error" role="alert">
            {player.error || listError}
          </p>
        ) : null}

        <section className="agent-voice-delivery">
          <h4 className="agent-voice-subh">Voice delivery</h4>
          <p className="agent-voice-sublead">
            Adjust how the voice is delivered during conversations.
          </p>

          <label className="agent-voice-slider">
            <span>
              Speed <em>{speed.toFixed(2)}</em>
            </span>
            <div className="agent-voice-slider-row">
              <input
                type="range"
                min={0.6}
                max={1.5}
                step={0.05}
                value={speed}
                onChange={(e) => patch({ ttsSpeed: clampTtsSpeed(e.target.value) })}
              />
              <input
                className="agent-conv-input agent-voice-num"
                type="number"
                min={0.6}
                max={1.5}
                step={0.05}
                value={speed}
                onChange={(e) => patch({ ttsSpeed: clampTtsSpeed(e.target.value) })}
              />
            </div>
          </label>

          <label className="agent-voice-slider">
            <span>
              Volume <em>{volume.toFixed(2)}</em>
            </span>
            <div className="agent-voice-slider-row">
              <input
                type="range"
                min={0.5}
                max={2}
                step={0.05}
                value={volume}
                onChange={(e) => patch({ ttsVolume: clampTtsVolume(e.target.value) })}
              />
              <input
                className="agent-conv-input agent-voice-num"
                type="number"
                min={0.5}
                max={2}
                step={0.05}
                value={volume}
                onChange={(e) => patch({ ttsVolume: clampTtsVolume(e.target.value) })}
              />
            </div>
          </label>

          <label className="agent-voice-emotion">
            <span>Default emotion</span>
            <Select
              triggerClassName="agent-conv-input"
              value={emotion}
              onChange={(v) => patch({ ttsEmotion: v || null })}
              groupBy
              options={[
                { value: "", label: "Auto — model reads the line" },
                ...TTS_EMOTION_PRIMARY.map((em) => ({
                  value: em,
                  label: em,
                  group: "Primary",
                })),
                ...TTS_EMOTION_MORE.map((em) => ({
                  value: em,
                  label: em,
                  group: "More",
                })),
              ]}
            />
            <em>
              Controls the emotional tone when no explicit emotion is specified. Emotion tags are
              available in English only.
            </em>
          </label>

          <button
            type="button"
            className="agent-prompt-reset"
            onClick={() => patch({ ttsSpeed: 1, ttsVolume: 1, ttsEmotion: null })}
          >
            <RotateCcw size={12} aria-hidden />
            Reset delivery
          </button>
        </section>
      </div>

      {libraryOpen && (
        <aside className="agent-voice-library" aria-label="Voice library">
          <header className="agent-voice-library-head">
            <div>
              <b>Choose a voice</b>
              <span>Browse and select a voice from the library.</span>
            </div>
            <button
              type="button"
              className="agent-edit-close"
              aria-label="Close library"
              onClick={() => setLibraryOpen(false)}
            >
              <X size={16} />
            </button>
          </header>

          <div className="agent-voice-filters">
            <label className="agent-voice-search">
              <Search size={14} aria-hidden />
              <input
                className="agent-conv-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, description or ID…"
                aria-label="Search voices"
              />
            </label>
            <div className="agent-voice-filter-row">
              <Select
                triggerClassName="agent-conv-input"
                size="sm"
                searchable={false}
                ariaLabel="Filter by gender"
                value={gender}
                onChange={(v) => setGender(v ?? "")}
                options={TTS_GENDER_OPTS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              <Select
                triggerClassName="agent-conv-input"
                size="sm"
                searchable={false}
                ariaLabel="Filter by language"
                value={language}
                onChange={(v) => setLanguage(v ?? "")}
                options={TTS_LANGUAGE_OPTS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </div>
          </div>

          {listError ? <p className="agent-voice-error" role="alert">{listError}</p> : null}

          <div className="agent-voice-list-wrap">
            {loading && voices.length === 0 ? (
              <div className="agent-voice-empty">
                <Loader2 className="spin" size={18} /> Loading voices…
              </div>
            ) : voices.length === 0 ? (
              <div className="agent-voice-empty">
                No voices match. Try clearing filters or check the Cartesia API key.
              </div>
            ) : (
              <ul className="agent-voice-list" role="listbox" aria-label="Voices">
                {voices.map((v) => {
                  const active = v.id === draft.voiceId;
                  const playKey = `stock:${v.id}`;
                  const playState = player.stateFor(playKey);
                  const title = voiceTitle(v);
                  const g = genderChip(v.gender);
                  const lang = languageChip(v);
                  return (
                    <li
                      key={v.id}
                      role="option"
                      aria-selected={active}
                      className={`agent-voice-row${active ? " is-active" : ""}`}
                    >
                      <VoicePlayButton
                        variant="outline"
                        playState={playState}
                        onClick={() => void player.playStockPreview(v.id)}
                      />
                      <button
                        type="button"
                        className="agent-voice-row-copy"
                        onClick={() => patch({ voiceId: v.id, voiceName: title })}
                      >
                        <span className="agent-voice-row-name">
                          {title}
                          {lang ? <span className="agent-voice-chip agent-voice-chip--lang">{lang}</span> : null}
                          {g ? (
                            <span className={`agent-voice-chip agent-voice-chip--${g.toLowerCase()}`}>
                              {g}
                            </span>
                          ) : null}
                        </span>
                        {(v.description || v.tagline) ? (
                          <span className="agent-voice-row-desc">{v.description || v.tagline}</span>
                        ) : null}
                        <code className="agent-voice-id">{v.id}</code>
                      </button>
                      <input
                        type="radio"
                        name="agent-voice-pick"
                        checked={active}
                        onChange={() => patch({ voiceId: v.id, voiceName: title })}
                        aria-label={`Select ${title}`}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {hasMore && (
            <button
              type="button"
              className="agent-voice-more"
              disabled={loadingMore}
              onClick={() => void loadVoices({ append: true, cursor: nextPage })}
            >
              {loadingMore ? <Loader2 size={14} className="spin" /> : <ChevronDown size={14} />}
              Load more
            </button>
          )}
        </aside>
      )}
    </div>
  );
}
